import { SelectionModel } from '@angular/cdk/collections';
import { NgFor, NgIf } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { PageEvent } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ToastrService } from 'ngx-toastr';
import { BreadcrumbComponent } from 'src/app/shared/components/breadcrumb/breadcrumb.component';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { QMSPaginatorComponent } from 'src/app/shared/components/qms-paginator/qms-paginator.component';
import { LogType } from 'src/app/shared/enum/log-type.enum';
import { BreadcrumbItem } from 'src/app/shared/models/breadcrumb.interface';
import { LogModel } from 'src/app/shared/models/log.model';
import { DateFormatPipe } from 'src/app/shared/pipes/date-format.pipe';
import { LogService } from 'src/app/shared/services/log.service';
import { DepartmentService } from '../../services/department.service';
import { SearchRoleFilter } from '../roles/models/search-role-filter.model';
import { DepartmentModel } from './models/department.model';

@Component({
  selector: 'app-departments',
  standalone: true,
  imports: [
    BreadcrumbComponent,
    DateFormatPipe,
    QMSPaginatorComponent,
    FormsModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    MatSelectModule,
    MatTableModule,
    MatSortModule,
    MatDatepickerModule,
    MatMenuModule,
    NgIf,
    NgFor,
    TranslateModule,
    AngularSvgIconModule,
    RouterModule,
  ],
  templateUrl: './departments.component.html',
  styleUrl: './departments.component.scss',
})
export class DepartmentsComponent {
  displayedColumns: string[] = ['select', 'name', 'location', 'totalEmployees', 'action'];
  dataSource = new MatTableDataSource<DepartmentModel>();
  selection = new SelectionModel<DepartmentModel>(true, []);

  totalCount = 0;

  breadcrumbItems: BreadcrumbItem[] = [];
  filter: SearchRoleFilter = new SearchRoleFilter();
  pageSizeOptions: number[] = [10, 25, 100];

  constructor(
    public router: Router,
    private departmentService: DepartmentService,
    private dialog: MatDialog,
    private translate: TranslateService,
    private toastr: ToastrService,
    private logService: LogService,
  ) {}

  ngOnInit(): void {
    this.breadcrumbItems = [
      {
        name: 'SIDE_BAR.HOME',
        url: '/',
      },
      {
        name: 'SIDE_BAR.MANAGE_DEPARTMENT',
        url: '/admin/departments',
      },
    ];

    this.loadData();
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.selection.select(...this.dataSource.data);
  }

  /** The label for the checkbox on the passed row */
  checkboxLabel(row?: DepartmentModel): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.id + 1}`;
  }

  pageEvent(event: PageEvent) {
    this.filter.pageIndex = event.pageIndex + 1;
    this.filter.pageSize = event.pageSize;
    this.loadData();
  }

  loadData() {
    this.departmentService.getDepartmentPaging(this.filter).subscribe((res) => {
      this.totalCount = res.totalCount;
      this.dataSource = new MatTableDataSource<DepartmentModel>(
        res.chemicalDepartment.map((item: any) => {
          const row = {
            id: item.id,
            name: item.name,
            location: item.path.replace(/[/]/g, ' > '),
            totalEmployees: item.totalEmployees || 0,
          };
          return row;
        }),
      );
    });
  }

  editDepartment(id: number) {
    this.router.navigate([`admin/departments/edit/${id}`]);
  }

  deleteDepartment(id: number) {
    const ids = id ? [id] : this.selection.selected.map((x) => x.id);
    if (this.checkDepartmentHasEmployee(ids)) {
      this.toastr.warning(this.translate.instant('DEPARTMENTS.CANNOT_DELETE_MESSAGE'));
      return;
    }

    const dialog = this.dialog.open(ConfirmDialogComponent, {
      data: {
        actionText: this.translate.instant('COMMON.CONFIRMED'),
        content: this.translate.instant('DEPARTMENTS.CONFIRM_DELETE_MESSAGE'),
      },
      width: '500px',
    });

    dialog.afterClosed().subscribe((confirm) => {
      if (confirm) {
        this.departmentService.deleteDepartments(ids).subscribe((res) => {
          this.toastr.success(this.translate.instant('DEPARTMENTS.DEPARTMENT_DELETED'));

          this.saveLogDeleteDepartment(this.dataSource.data.find((x) => x.id === id)?.name || '');
          this.loadData();
        });
      }
    });
  }

  saveLogDeleteDepartment(name: string): void {
    const log: LogModel = new LogModel();
    log.logType = LogType.deleteDepartment;
    log.description = ` remove department  <strong>${name}</strong>`;
    log.descriptionNo = ` fjerne avdelingen <strong>${name}</strong>`;
    this.logService.saveLog(log).subscribe();
  }

  checkDepartmentHasEmployee(ids: number[]): boolean {
    console.log(ids, this.dataSource.data);
    return ids.some((id) => this.dataSource.data.find((item) => item.id === id)?.totalEmployees || 0 > 0);
  }

  clearFilter(): void {
    console.log('adsfasf');
    this.filter.keyWord = '';
    this.searchByKeyword();
  }

  searchByKeyword(): void {
    this.filter.pageIndex = 0;
    this.filter.pageIndex = 0;
    this.loadData();
  }
}
